/**
 * Maps whatever a schedule mutation threw into one translation key, so every caller shows the same
 * message for the same problem (KIWI-6576).
 */
export type ScheduleErrorInfo = {
  /** Translation key to show. */
  messageKey: string;
  /** The backend's own text, when it is more useful than a generic key. */
  rawMessage?: string;
};

const isText = (value: unknown): value is string => typeof value === "string" && value.length > 0;

/** Text the backend itself wrote: safe to show an editor as is. */
const collectBackendMessages = (error: any): string[] =>
  [
    error?.graphQLErrors?.[0]?.message,
    error?.networkError?.result?.error?.message,
    error?.networkError?.result?.errors?.[0]?.message,
  ].filter(isText);

/** Everything worth matching against, including codes and the client library's own wording. */
const collectAllMessages = (error: any): string[] =>
  [
    ...collectBackendMessages(error),
    error?.graphQLErrors?.[0]?.extensions?.code,
    error?.networkError?.result?.error?.extensions?.code,
    error?.message,
  ].filter(isText);

export const getScheduleErrorInfo = (error: any): ScheduleErrorInfo => {
  const joined = collectAllMessages(error).join(" | ");
  const statusCode = error?.networkError?.statusCode;

  if (statusCode === 401 || statusCode === 403 || /unauthor|UNAUTHENTICATED/i.test(joined)) {
    return { messageKey: "schedule_permission_error" };
  }
  if (/already trigger is initiated/i.test(joined)) {
    return { messageKey: "schedule_already_exists" };
  }
  if (/does not exist/i.test(joined)) {
    return { messageKey: "schedule_not_found" };
  }
  // The backend's own validation text ("... must be in the future!") is already user-readable.
  // Apollo's generic wording ("Response not successful: Received status code 500") is NOT, so a
  // plain `error.message` is deliberately never surfaced as raw text.
  const [backendText] = collectBackendMessages(error);
  return backendText
    ? { messageKey: "api_error_toast", rawMessage: backendText }
    : { messageKey: "api_error_toast" };
};

/**
 * The text to show for a failed schedule call: a known problem's translation, else the backend's
 * own wording, else the generic API error.
 */
export const getScheduleErrorText = (error: any, t: (key: string) => string): string => {
  const { messageKey, rawMessage } = getScheduleErrorInfo(error);
  return messageKey === "api_error_toast" && rawMessage ? rawMessage : t(messageKey);
};
